import React, { useEffect, useMemo, useState } from 'react';
import { PackageCheck, RefreshCw, Warehouse } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import { EmployeeHoldingDetail, EmployeeHoldingSKUGroup, getMyHolding } from '../services/api';
import { formatWeight } from '../utils/formatters';
import { navigateTo } from '../utils/navigation';
import { notifyError } from '../utils/pdaFeedback';

function normalizeList(list?: EmployeeHoldingSKUGroup[]) {
  return Array.isArray(list) ? list : [];
}

function getDisplayName(data: EmployeeHoldingDetail | null) {
  if (!data) {
    return '--';
  }
  return data.full_name || data.username || '--';
}

function MyHolding() {
  const [data, setData] = useState<EmployeeHoldingDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const items = useMemo(() => normalizeList(data?.items), [data]);

  async function loadHolding() {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await getMyHolding();
      setData(response.data || null);
    } catch (error: any) {
      const message = error?.msg || '我的虚拟库加载失败';
      setErrorMessage(message);
      notifyError(message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadHolding();
  }, []);

  return (
    <PdaPage
      actionIcon={<RefreshCw aria-hidden="true" size={13} strokeWidth={2.4} />}
      actionLabel={loading ? '刷新中' : '刷新'}
      onAction={() => void loadHolding()}
      onBack={() => navigateTo('/home')}
      pageClassName="my-holding-page"
      title="我的虚拟库"
    >
      {errorMessage ? (
        <section className="holding-alert">
          {errorMessage}
        </section>
      ) : null}

      <section className="holding-hero">
        <div className="holding-hero__head">
          <div>
            <p>当前持料人</p>
            <strong>{getDisplayName(data)}</strong>
          </div>
          <span>{data?.emp_no || '--'}</span>
        </div>
        <div className="holding-hero__stats">
          <div>
            <PackageCheck aria-hidden="true" size={20} strokeWidth={2.3} />
            <strong>{data?.total_pieces ?? 0}</strong>
            <span>持料件数</span>
          </div>
          <div>
            <Warehouse aria-hidden="true" size={20} strokeWidth={2.3} />
            <strong>{formatWeight(data?.total_weight)}kg</strong>
            <span>持料总重</span>
          </div>
        </div>
      </section>

      <section className="holding-panel">
        <div className="holding-panel__title">
          <span>持料明细</span>
          <em>{items.length} 个 SKU</em>
        </div>

        {loading && !data ? (
          <div className="holding-empty">加载中...</div>
        ) : null}

        {!loading && !items.length ? (
          <div className="holding-empty">
            <PackageCheck aria-hidden="true" size={48} strokeWidth={1.8} />
            <strong>暂无持料</strong>
            <span>当前没有已领未上架的物料</span>
          </div>
        ) : null}

        {items.length ? (
          <div className="holding-list">
            {items.map((item) => (
              <article className="holding-card" key={item.sku || item.bids.join(',')}>
                <div className="holding-card__top">
                  <strong>{item.sku || '--'}</strong>
                  <span>{formatWeight(item.total_weight)}kg</span>
                </div>
                <div className="holding-card__meta">
                  <span>{item.pieces || 0} 件</span>
                  <span>{item.bids?.length || 0} 个 BID</span>
                </div>
                {item.bids?.length ? (
                  <div className="holding-bids">
                    {item.bids.map((bid) => (
                      <span key={bid}>{bid}</span>
                    ))}
                  </div>
                ) : (
                  <div className="holding-bids holding-bids--empty">暂无 BID 明细</div>
                )}
              </article>
            ))}
          </div>
        ) : null}
      </section>

      <section className="holding-note">
        <strong>持料说明</strong>
        <span>已从仓库领出、还没上架到工位的物料会显示在这里。上架到工位后，物料会离开你的虚拟库。</span>
      </section>
    </PdaPage>
  );
}

export default MyHolding;
